/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { forwardRef, Ref, RefAttributes, useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from "react-redux";
import { useHistory } from 'react-router-dom';

import { Box, Button, CircularProgress, Container, Grid, Tooltip, Typography } from "@material-ui/core";
import {
    AddRounded, ChevronLeft, ChevronRight, Info, FileCopyRounded, CheckCircleRounded
} from "@material-ui/icons";
import classNames from "classnames";
import MaterialTable, { Column } from "@material-table/core";

import {
    ApplicationReducer, ApplicationState, SubscribedApiScopesReducer, Subscription, SubscriptionsReducer, UserReducer
} from "../../../data/interfaces";
import { deleteSubscription, fetchSubscribedApiScopes, fetchSubscriptions } from "../../../data/operations";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import { PrimaryButtonRounded } from "../../../elements/PrimaryButtonRounded";
import { Status } from "../../../elements/Status";
import AppInfoBar from "../../shared/AppInfoBar";
import { ConfirmationDialog } from "../../shared/ConfirmationDialog/ConfirmationDialog";

import CreateSubscription from "./CreateSubscriptionDialog";
import EditSubscriptionDialog from "./EditSubscriptionDialog";
import "./style.scss";
import { useStyles } from './styles';
import {PATH_SEPARATOR} from "../../../data/constants";
import {trackAddingAppSubscription} from "../../../util/app-insights";
import CustomIcons from '../../../icons/CustomIcons';
import Settings from '../../../conf/settings';
import { ApplicationPermission } from '../../../util/app-permissions';
import { AppPermission } from '../../../data/emus';

interface RowAttributes {
  apiId?: string;
  api?: string;
  version?: string;
  lifecycleStatus?: string;
  subscriptionTier?: string;
  requestedSubscriptionTier?: string;
  subscriptionStatus?: string;
  subscription: Subscription;
}

interface AppSubscriptionsProps {
    applicationReducer: ApplicationReducer,
    subscriptionReducer: SubscriptionsReducer,
    userReducer: UserReducer,
    subscribedApiScopesReducer: SubscribedApiScopesReducer,
    dispatchFetchSubscriptionsList: (applicationId: string, organizationId: string, type: string, 
        limit?: number, offset?: number) => void,
    dispatchDeleteSubscription: (subscriptionId: string) => void,
    dispatchFetchSubscribedApiScopes: (organizationId: string, apiIds: string[]) => Promise<string[]|null>,
}

export const formatSubscriptionApiVersion = (apiVersion: string, versionRange = true): string => {
    if (versionRange) {
        const majorVersionRange = getApiMajorVersionRange(apiVersion);
        return majorVersionRange ? majorVersionRange.concat(".*") : apiVersion;
    } else {
        return apiVersion;
    }
}

export const formattedLifeCycleStatus = (text: string): string => {
  const words = text.split(" ");
  let output = "";
  for (const word of words) {
    if (word.length > 0) {
      output += word[0].toUpperCase() + word.slice(1).toLowerCase() + " ";
    }
    return output.trim();
  }
  return output;
};

export const getApiMajorVersionRange = (version: string) => {
    if (version.startsWith("v") && Settings.intelligentRoutingFeatureEnabled) {
        return version.split(".")[0];
    } else {
        return null;
    }
}

export const Version = (versionProps: { version: string }) => {
    const { version } = versionProps;
    return (
        <Box display="flex">
            <Typography variant="body1">
                {version.replace(".*", "")}
            </Typography>
        </Box>
    );
}

export const Plan = ({ rowData }: { rowData: RowAttributes }) => {
  const { subscriptionTier, requestedSubscriptionTier, subscriptionStatus } =
    rowData;
  const tooltipContent = (
    <>
      <Box>Current subscription: {subscriptionTier}</Box>
      {subscriptionStatus === "TIER_UPDATE_PENDING" && (
        <Box>Pending Update: {requestedSubscriptionTier}</Box>
      )}
    </>
  );
  return (
    <Box display="flex">
      <Tooltip title={tooltipContent} placement="top">
        <Typography variant="body1">{subscriptionTier}</Typography>
      </Tooltip>
    </Box>
  );
};


function AppSubscriptions(props: AppSubscriptionsProps) {
    const {
        applicationReducer, subscriptionReducer, subscribedApiScopesReducer, dispatchFetchSubscriptionsList,
        dispatchDeleteSubscription, userReducer, dispatchFetchSubscribedApiScopes,
    } = props;
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const isFetchingSubscriptions = subscriptionReducer.isLoading;
    const isDeletingSubscription = subscriptionReducer.isDeletingSubscription;
    const subscriptionList = subscriptionReducer.subscriptions;
    const selectedOrgHandleName = userReducer.selectedOrg.handle;
    const { userInfo, user } = userReducer;
    const scopes = user.allowedScopes
    const { loading: isSubscribedApiScopesLoading } = subscribedApiScopesReducer;
    const classes = useStyles();
    const history = useHistory();
    const intl = useIntl();
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription>();
    const [openNewSubscriptionWizard, setOpenNewSubscriptionWizard] = useState(false);
    const [openDeleteSubscriptionWizard, setOpenDeleteSubscriptionWizard] = useState(false);
    const [openEditSubscriptionWizard, setOpenEditSubscriptionWizard] = useState(false);
    const [copiedIconVisible, setCopiedIconVisible] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(0);

    let data: RowAttributes[] = [];
    if (subscriptionList?.count > 0) {
        data = subscriptionList.list.map((subscription: Subscription) => {
            return {
                apiId: subscription.apiInfo?.versionInfo?.majorRange?.latestVersionAPIId || subscription.apiInfo?.id,
                api: subscription.apiInfo?.displayName,
                version: formatSubscriptionApiVersion(subscription.apiInfo?.version || '', !!subscription?.versionRange),
                lifecycleStatus: formattedLifeCycleStatus(subscription.apiInfo?.lifeCycleStatus ?? ""),
                subscriptionTier: subscription.throttlingPolicy,
                requestedSubscriptionTier: subscription.requestedThrottlingPolicy,
                subscriptionStatus: subscription.status,
                subscription
            }
        });
    }
    const pageSize = data.length > 5 ? 5 : data.length;

    useLayoutEffect(() => {
        if (application?.applicationId) {
            const organizationId = userReducer.selectedOrg.uuid;
            /* Todo - Keep this limit and offset as fixed values for now due to the urgency of the fix. 
                This will be fixed in near future.    
            */
            dispatchFetchSubscriptionsList(application?.applicationId, organizationId, 'application', 150, 0);
        }
    }, [application]);

    const onClickDeleteSubscription = (subscription: Subscription) => {
        setSelectedSubscription(subscription);
        setOpenDeleteSubscriptionWizard(true);
    };

    const onClickDeleteSubscriptionDialogOk = async () => {
        await dispatchDeleteSubscription(selectedSubscription?.subscriptionId as string);
        setOpenDeleteSubscriptionWizard(false);
    };

    const onClickDeleteSubscriptionDialogCancel = () => {
        setOpenDeleteSubscriptionWizard(false);
    };

    const onClickEditSubscription = (subscription: Subscription) => {
        history.push(
            PATH_SEPARATOR + 
            selectedOrgHandleName + 
            PATH_SEPARATOR + 
            'applications' + 
            PATH_SEPARATOR + 
            application?.applicationId + 
            '/edit-subscription/' + subscription.subscriptionId);
    };

    const onClickEditSubscriptionDialogCancel = () => {
        setOpenEditSubscriptionWizard(false);
    };

    const onClickCreateSubscriptionDialogCancelButton = () => {
        setOpenNewSubscriptionWizard(false);
    };

    const onClickCreateSubscription = () => {
        history.push(
            PATH_SEPARATOR + 
            selectedOrgHandleName + 
            PATH_SEPARATOR + 
            'applications' + 
            PATH_SEPARATOR + 
            application?.applicationId + 
            '/new-subscriptions');
        
    };

    const onClickCopyScopes = async () => {
        const apiIds = subscriptionList.list.map((subscription) => { return subscription.apiInfo?.id });
        const scopes = await dispatchFetchSubscribedApiScopes(userReducer.selectedOrg.uuid, apiIds as string[]);
        if (scopes) {
            const scopesToCopy = scopes.join(' ');
            navigator.clipboard.writeText(scopesToCopy);
        }
        setCopiedIconVisible(true);
        setTimeout(() => {
            setCopiedIconVisible(false);
        }, 2000);
    };

    const createSubscriptionText = intl.formatMessage({
        id: 'application.subscriptions.add.button.label',
        defaultMessage: 'Add APIs'
    });

    const copyScopesText = intl.formatMessage({
        id: 'components.app.appSubscriptions.index.copyScopes.button.label',
        defaultMessage: 'Copy Scopes'
    });

    const apiColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.api',
        defaultMessage: 'API'
    });

    const versionColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.version',
        defaultMessage: 'VERSION'
    });

    const lcStatusColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.lifecycleStatus',
        defaultMessage: 'LIFECYCLE STATUS'
    });

    const planColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.plan',
        defaultMessage: 'SUBSCRIPTION PLAN'
    });

    const statusColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.status',
        defaultMessage: 'STATUS'
    });

    const actionsColumn = intl.formatMessage({
        id: 'application.subscriptions.table.column.actions',
        defaultMessage: 'ACTIONS'
    });

    const confirmationDialogI18n = {
        title: intl.formatMessage({
            id: 'application.subscriptions.deleteConfirmationDialog.title',
            defaultMessage: 'Delete Subscription'
        }),
        content: intl.formatMessage({
            id: 'application.subscriptions.deleteConfirmationDialog.content',
            defaultMessage: 'Are you sure you want to delete the subscription?'
        }),
        okayButtonLabel: intl.formatMessage({
            id: 'application.subscriptions.deleteConfirmationDialog.okButtonLabel',
            defaultMessage: 'Delete'
        }),
        cancelButtonLabel: intl.formatMessage({
            id: 'application.subscriptions.deleteConfirmationDialog.cancelButtonLabel',
            defaultMessage: 'Cancel'
        }),
        loadingText: intl.formatMessage({
            id: 'application.subscriptions.deleteConfirmationDialog.loadingText',
            defaultMessage: 'Deleting...'
        })
    };

    const getNextPageIcon = (props: RefAttributes<SVGSVGElement>,
                             ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />;

    const getPreviousPageIcon = (props: RefAttributes<SVGSVGElement>,
                                 ref: Ref<SVGSVGElement>) => <ChevronLeft {...props} ref={ref} />;


    const ApiColumn = (rowData: RowAttributes) => {
        return (
            <Typography
                onClick={() => history.push(PATH_SEPARATOR + selectedOrgHandleName + '/apis/' + rowData.apiId)}
                className={classes.apiNameColumn}
            >
                {rowData.api}
            </Typography>
        );
    };

    const StatusColumn = (rowData: RowAttributes) => {
        return (
            <Status type={rowData.subscriptionStatus?.toLowerCase()} />
        );
    };

    const ActionsColumn = (rowData: RowAttributes) => {
        return (
            <Actions subscription={rowData.subscription} />
        );
    };

    const VersionColumn = (rowData: RowAttributes) => {
        return (
            <Version version={rowData.version || ''} />
        );
    };

    const PlanColumn = (rowData: RowAttributes) => {
      return <Plan rowData={rowData} />;
    };

    <CustomIcons icon="badge-what" />

    const createBtnDisableToolTip = intl.formatMessage({
        id: 'application.subscriptions.add.button.lackOfPermissionTooltip',
        defaultMessage: "You don't have permission to add subscriptions from this application"
    });

    const CreateBtn = (disabled?: boolean) => {
        return (
            <div className={classes.actions}>
                <Tooltip title={disabled ? createBtnDisableToolTip : ''}>
                    <span>
                        <PrimaryButtonRounded
                            dataTestId="create-subscription-btn"
                            onClick={onClickCreateSubscription}
                            startIcon={<AddRounded />}
                            text={createSubscriptionText}
                            className={classes.addApiBtn}
                            disabled={disabled}
                        />
                    </span>
                </Tooltip>
            </div>
            
        );
    };

    const CopyScopesBtn = () => {
        return (
            <PrimaryButtonRounded
                dataTestId="copy-scopes-btn"
                onClick={onClickCopyScopes}
                startIcon={copiedIconVisible ? <CheckCircleRounded /> : <FileCopyRounded />}
                text={copyScopesText}
                className={classes.copyScopesBtn}
                disabled={isSubscribedApiScopesLoading}
            />
        );
    };

    const DeleteBtn = (subscription: Subscription, disabled?: boolean) => {
        return (
            <Button
                data-testid={"subscription-delete-btn" + subscription.subscriptionId}
                disableRipple={true}
                disableTouchRipple={true}
                onClick={() => onClickDeleteSubscription(subscription)}
                className={classNames(classes.deleteBtn, {
                    [classes.disabled]: disabled
                })}
                disabled={disabled}
            >
                <Typography className={classes.btnText}>
                    <FormattedMessage id='application.subscriptions.delete.button.label' defaultMessage='Delete' />
                </Typography>
            </Button>
        );
    };

    const EditBtn = (subscription: Subscription, disabled?: boolean) => {
        return (
            <Button
                data-testid={"subscription-edit-btn" + subscription.subscriptionId}
                disableRipple={true}
                disableTouchRipple={true}
                onClick={() => onClickEditSubscription(subscription)}
                className={classNames(classes.hoverBtn, classes.editBtn, {
                    [classes.disabled]: disabled
                })}
                disabled={disabled}
            >
                <Typography className={classes.btnText}>
                    <FormattedMessage id='application.subscriptions.edit.button.label' defaultMessage='Edit' />
                </Typography>
            </Button>
        );
    };

    const editButtonDisabledDueToPermissionTooltip = intl.formatMessage({
        id: 'application.subscriptions.edit.button.lackOfPermissionTooltip',
        defaultMessage: "You don't have permission to edit this subscription"
    });

    const deleteButtonDisabledDueToPermissionTooltip = intl.formatMessage({
        id: 'application.subscriptions.delete.button.lackOfPermissionTooltip',
        defaultMessage: "You don't have permission to delete this subscription"
    });

    const editButtonDisabledDueToBlockedTooltip = intl.formatMessage({
        id: 'application.subscriptions.edit.button.subscriptionDisabledTooltip',
        defaultMessage: "Editing is only allowed for active subscriptions"
    });

    const Actions = (btnProps: {subscription: Subscription}) => {
        const { subscription } = btnProps;
        const hasPermission = ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, scopes);
        const isEditDisabled = (subscription.status !== "UNBLOCKED" && subscription.status !== "APPROVED") || !hasPermission;
        const isDeleteDisabled = !hasPermission;
        
        const getEditTooltipText = () => {
            if (!hasPermission) {
                return editButtonDisabledDueToPermissionTooltip;
            }
            return isEditDisabled ? editButtonDisabledDueToBlockedTooltip : "";
        };

        return (
            <div>
                <div className={classes.actions}>
                    <Tooltip title={getEditTooltipText()}>
                        <span>
                            {EditBtn(subscription, isEditDisabled)}
                        </span>
                    </Tooltip>
                    <Tooltip title={!hasPermission && deleteButtonDisabledDueToPermissionTooltip || ""}>
                        <span>
                            {DeleteBtn(subscription, isDeleteDisabled)}
                        </span>
                    </Tooltip>
                </div>
            </div>
        );
    };

    const tableIcons = {
        NextPage: forwardRef(getNextPageIcon),
        PreviousPage: forwardRef(getPreviousPageIcon)
    };

    const columns = [
        { title: apiColumn, field: 'api', width: '25%', render: ApiColumn },
        { title: versionColumn, field: 'version', width: '10%', render: VersionColumn },
        { title: lcStatusColumn, field: 'lifecycleStatus', width: '15%' },
        { title: planColumn, field: 'subscriptionTier', width: '18%', render: PlanColumn },
        { title: statusColumn, field: 'subscriptionStatus', width: '16%', render: StatusColumn },
        { title: actionsColumn, width: '16%', render: ActionsColumn }
    ] as Column<RowAttributes>[];

    const SubscriptionTable = () => {
        if (data.length > 0 && !isFetchingSubscriptions) {
            return (
                <div>
                    <div className={["config-list", classes.containerList].join(" ")}>
                        <MaterialTable
                            page={currentPage}
                            isLoading={false}
                            columns={columns}
                            options={{
                                sorting: false,
                                showTitle: false,
                                search: false,
                                pageSize,
                                actionsColumnIndex: -1,
                                showFirstLastPageButtons: false,
                                tableLayout: 'fixed',
                                headerStyle: {height: 30},
                                rowStyle: {height: 45},
                            }}
                            data={data}
                            icons={tableIcons}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className={classes.noDataInfo}>
                    <Box display="flex" flexDirection="column" justifyContent="space-between">
                        <Box display="flex" flexDirection="row" justifyContent="space-between">
                            <Box>
                                <Typography variant='subtitle2' className={classes.noDataText}>
                                    <FormattedMessage
                                        id='application.subscriptions.table.empty.label'
                                        defaultMessage='No subscriptions available for this application'
                                    />
                                </Typography>
                            </Box>
                            <Box display="flex">
                                <Info className={classes.infoIcon} />
                            </Box>
                        </Box>
                    </Box>
                </div>
            );
        }
    };

    const SubscriptionContent = () => {
        if (isFetchingSubscriptions) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            const hasCreatePermission = ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, scopes);
            return (
                <Container maxWidth="lg" className={classes.container} classes={{ root: classes.componentPadding }}>
                    <Grid container={true} spacing={3}>
                        <Grid item={true} xs={12} md={12} lg={9}>
                            <div className={classes.titleWrapper}>
                                <Typography className={classes.title}>
                                    <FormattedMessage
                                        id='application.subscriptions.title'
                                        defaultMessage='Subscription Management'
                                    />
                                </Typography>
                                {CreateBtn(!hasCreatePermission)}
                                {subscriptionList.list?.length > 0 && <CopyScopesBtn />}
                                {isSubscribedApiScopesLoading && <CircularProgress size={18} />}
                            </div>
                        </Grid>
                        <Grid item={true} xs={12} md={12} lg={12}>
                            <SubscriptionTable />
                        </Grid>
                    </Grid>
                </Container>
            );
        }
    };

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <AppInfoBar />
            <SubscriptionContent />
            {openNewSubscriptionWizard &&
                <CreateSubscription
                    open={openNewSubscriptionWizard}
                    onClose={onClickCreateSubscriptionDialogCancelButton}
                />
            }
            {openEditSubscriptionWizard &&
                <EditSubscriptionDialog
                    open={openEditSubscriptionWizard}
                    onClose={onClickEditSubscriptionDialogCancel}
                    subscription={selectedSubscription as Subscription}
                />
            }
            {openDeleteSubscriptionWizard &&
                <ConfirmationDialog
                    open={openDeleteSubscriptionWizard}
                    dialogTitle={confirmationDialogI18n.title}
                    dialogContentText={confirmationDialogI18n.content}
                    okayButtonLabel={confirmationDialogI18n.okayButtonLabel}
                    cancelButtonLabel={confirmationDialogI18n.cancelButtonLabel}
                    isActionProcessing={isDeletingSubscription}
                    loadingActionHelperText={confirmationDialogI18n.loadingText}
                    onClose={onClickDeleteSubscriptionDialogCancel}
                    onCancel={onClickDeleteSubscriptionDialogCancel}
                    onOkay={onClickDeleteSubscriptionDialogOk}
                />
            }
        </main>
    )
}

const mapDispatchToProps = {
    dispatchFetchSubscriptionsList: fetchSubscriptions,
    dispatchDeleteSubscription: deleteSubscription,
    dispatchFetchSubscribedApiScopes: fetchSubscribedApiScopes,
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        subscriptionReducer: state.subscriptionsReducer,
        subscribedApiScopesReducer: state.subscribedApiScopesReducer,
        userReducer: state.userReducer
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(AppSubscriptions);
